import {calculateErrorMessage} from 'common/helpers'

type ApiResponse = any
type ParamMap = Record<string, string | number | undefined>

const replaceParamsInPath = (path: string, params: ParamMap = {}) =>
  path.replace(/:([a-zA-Z0-9_]+)/g, (_, k) => (params[k] != null ? String(params[k]) : `:${k}`))

const closeChatIfOpen = () => {
  try {
    const closePortalBtn = document.querySelector(
      'button[aria-label="Close chatbot portal"]'
    ) as HTMLElement
    if (closePortalBtn) {
      closePortalBtn.click()
      return
    }

    const minimizeBtn = document.querySelector(
      'button[aria-label="Minimize chatbot portal"]'
    ) as HTMLElement
    if (minimizeBtn) {
      minimizeBtn.click()
      return
    }

    const drawer = document.getElementById('kt_drawer_chat')
    const closeBtn = document.getElementById('kt_drawer_chat_close')
    const toggleBtn = document.getElementById('kt_drawer_chat_toggle')

    const isOpen = !!(
      drawer &&
      (drawer.classList.contains('show') ||
        drawer.getAttribute('data-kt-drawer-show') === 'true' ||
        drawer.getBoundingClientRect().width > 0)
    )

    if (isOpen) {
      if (closeBtn && typeof (closeBtn as HTMLElement).click === 'function') {
        ;(closeBtn as HTMLElement).click()
      } else if (toggleBtn && typeof (toggleBtn as HTMLElement).click === 'function') {
        ;(toggleBtn as HTMLElement).click()
      } else if (drawer) {
        drawer.classList.remove('show')
      }
    }
  } catch (err) {
    calculateErrorMessage(err)
  }
}

const navigateFallback = async (path?: string) => {
  if (!path || typeof window === 'undefined') return
  try {
    const url = new URL(path, window.location.origin)
    const resolvedPath = url.pathname + url.search + url.hash
    if (window.location.pathname !== url.pathname || window.location.hash !== url.hash) {
      window.history.pushState({}, '', resolvedPath)
      window.dispatchEvent(new PopStateEvent('popstate'))
      await new Promise((r) => setTimeout(r, 400))
    }
  } catch (err) {
    calculateErrorMessage(err)
  }
}

function normalizeSelectorForQuery(sel: string): string | null {
  if (!sel) return null
  try {
    if (sel.includes(':has(')) {
      const tokens = sel.split(/[[\]\s>+~]+/).filter(Boolean)
      const last = tokens[tokens.length - 1]
      return last || null
    }
    return sel
  } catch (err) {
    calculateErrorMessage(err)
    return null
  }
}

async function waitForElementStable(
  selector: string,
  options?: {
    timeoutMs?: number
    pollIntervalMs?: number
    stableMs?: number
    waitForever?: boolean
    pathCheck?: string
  }
): Promise<{
  success: boolean
  elapsed: number
  reason?: string
  element?: HTMLElement | null
}> {
  const timeoutMs = options?.timeoutMs ?? 15000
  const pollIntervalMs = options?.pollIntervalMs ?? 150
  const stableMs = options?.stableMs ?? 500
  const waitForever = !!options?.waitForever
  const pathCheck = options?.pathCheck

  const start = Date.now()
  let aborted = false
  const abortHandler = () => {
    aborted = true
  }
  window.addEventListener('walkthrough-cancel', abortHandler)

  const norm = normalizeSelectorForQuery(selector)
  if (!norm) {
    window.removeEventListener('walkthrough-cancel', abortHandler)
    return {
      success: false,
      elapsed: Date.now() - start,
      reason: 'invalid-selector',
    }
  }

  const pollPresence = async (): Promise<HTMLElement | null> => {
    if (pathCheck && window.location.pathname !== pathCheck) return null
    try {
      const el = document.querySelector(norm) as HTMLElement | null
      if (el) return el
    } catch (err) {
      calculateErrorMessage(err)
      return null
    }
    return null
  }

  const waitForStability = (root: HTMLElement) =>
    new Promise<{ok: boolean}>((resolve) => {
      let lastMutation = Date.now()
      const mo = new MutationObserver(() => {
        lastMutation = Date.now()
      })
      try {
        mo.observe(root, {
          subtree: true,
          childList: true,
          attributes: true,
          characterData: true,
        })
      } catch (e) {
        calculateErrorMessage(e)
      }

      const check = () => {
        if (aborted) {
          try {
            mo.disconnect()
          } catch (e) {
            calculateErrorMessage(e)
          }
          return resolve({ok: false})
        }
        if (Date.now() - lastMutation >= stableMs) {
          try {
            mo.disconnect()
          } catch (e) {
            calculateErrorMessage(e)
          }
          return resolve({ok: true})
        }
        setTimeout(check, 80)
      }
      setTimeout(check, 80)
    })

  while (true) {
    if (aborted) {
      window.removeEventListener('walkthrough-cancel', abortHandler)
      return {success: false, elapsed: Date.now() - start, reason: 'aborted'}
    }
    if (pathCheck && window.location.pathname !== pathCheck) {
      window.removeEventListener('walkthrough-cancel', abortHandler)
      return {
        success: false,
        elapsed: Date.now() - start,
        reason: 'path-mismatch',
      }
    }

    const el = await pollPresence()
    if (el) {
      const stableRes = await waitForStability(el)
      if (stableRes.ok) {
        window.removeEventListener('walkthrough-cancel', abortHandler)
        return {
          success: true,
          elapsed: Date.now() - start,
          reason: 'element-stable',
          element: el,
        }
      } else {
        window.removeEventListener('walkthrough-cancel', abortHandler)
        return {success: false, elapsed: Date.now() - start, reason: 'aborted'}
      }
    }

    if (!waitForever && Date.now() - start >= timeoutMs) {
      window.removeEventListener('walkthrough-cancel', abortHandler)
      return {success: false, elapsed: Date.now() - start, reason: 'timeout'}
    }

    await new Promise((r) => setTimeout(r, Math.min(700, pollIntervalMs)))
  }
}

async function waitForPageContent(
  timeoutMs = 15000,
  intervalMs = 150,
  pathCheck?: string,
  preferredSelectors?: string[],
  componentName?: string,
  waitForever = false
): Promise<{success: boolean; elapsed: number; reason?: string}> {
  const start = Date.now()

  let eventResolved = false
  const evHandler = (e: Event) => {
    try {
      const d = (e as CustomEvent).detail || {}
      if (!componentName || !d.componentName || d.componentName === componentName)
        eventResolved = true
    } catch (e) {
      calculateErrorMessage(e)
    }
  }
  window.addEventListener('walkthrough-ready', evHandler as EventListener, {
    once: true,
  })

  if (preferredSelectors && preferredSelectors.length > 0) {
    if (eventResolved) {
      window.removeEventListener('walkthrough-ready', evHandler as EventListener)
      return {
        success: true,
        elapsed: Date.now() - start,
        reason: 'walkthrough-ready-event',
      }
    }

    for (const raw of preferredSelectors) {
      if (!raw) continue
      const normalized = normalizeSelectorForQuery(raw)
      if (!normalized) continue
      const res = await waitForElementStable(normalized, {
        timeoutMs,
        pollIntervalMs: intervalMs,
        stableMs: 500,
        waitForever,
        pathCheck,
      })
      if (res.success) {
        window.removeEventListener('walkthrough-ready', evHandler as EventListener)
        return {
          success: true,
          elapsed: Date.now() - start,
          reason: 'preferred-stable:' + normalized,
        }
      }
    }

    window.removeEventListener('walkthrough-ready', evHandler as EventListener)
    return {
      success: false,
      elapsed: Date.now() - start,
      reason: 'preferred-not-found',
    }
  }

  const heuristics = [
    '.card-xl-stretch',
    '.card',
    'main',
    '.app-main',
    '.page-content',
    '#root .card',
  ]
  for (const s of heuristics) {
    try {
      const el = document.querySelector(s)
      if (el) {
        const stable = await waitForElementStable(s, {
          timeoutMs: Math.min(2000, timeoutMs),
          pollIntervalMs: intervalMs,
          stableMs: 300,
          waitForever: false,
          pathCheck,
        })
        if (stable.success)
          return {
            success: true,
            elapsed: Date.now() - start,
            reason: 'heuristic:' + s,
          }
      }
    } catch (e) {
      calculateErrorMessage(e)
    }
  }

  while (true) {
    if (pathCheck && window.location.pathname !== pathCheck)
      return {
        success: false,
        elapsed: Date.now() - start,
        reason: 'path-mismatch',
      }
    if (eventResolved) {
      window.removeEventListener('walkthrough-ready', evHandler as EventListener)
      return {
        success: true,
        elapsed: Date.now() - start,
        reason: 'walkthrough-ready-event',
      }
    }

    for (const s of heuristics) {
      try {
        const el = document.querySelector(s)
        if (el) {
          const stable = await waitForElementStable(s, {
            timeoutMs: Math.min(2000, timeoutMs),
            pollIntervalMs: intervalMs,
            stableMs: 300,
            waitForever: false,
            pathCheck,
          })
          if (stable.success) {
            window.removeEventListener('walkthrough-ready', evHandler as EventListener)
            return {
              success: true,
              elapsed: Date.now() - start,
              reason: 'heuristic-late:' + s,
            }
          }
        }
      } catch (e) {
        calculateErrorMessage(e)
      }
    }

    if (!waitForever && Date.now() - start >= timeoutMs) {
      window.removeEventListener('walkthrough-ready', evHandler as EventListener)
      return {success: false, elapsed: Date.now() - start, reason: 'timeout'}
    }

    await new Promise((r) => setTimeout(r, Math.min(700, intervalMs * 1.5)))
  }
}

async function navigateAndWaitForContent(
  resolvedPath: string,
  attempts = 2,
  waitBetweenMs = 500,
  preferredSelectors?: string[],
  waitForever = false,
  componentName?: string
): Promise<{success: boolean; elapsed?: number; reason?: string}> {
  const startAll = Date.now()
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      await navigateFallback(resolvedPath)
      await new Promise((r) => setTimeout(r, 120))
      const result = await waitForPageContent(
        waitForever ? 0 : 15000,
        150,
        new URL(resolvedPath, window.location.origin).pathname,
        preferredSelectors,
        componentName,
        waitForever
      )

      if (result.success) {
        return {
          success: true,
          elapsed: Date.now() - startAll,
          reason: result.reason,
        }
      }
    } catch (err) {
      calculateErrorMessage(err)
    }

    if (attempt < attempts) {
      await new Promise((r) => setTimeout(r, waitBetweenMs))
    }
  }

  return {
    success: false,
    elapsed: Date.now() - startAll,
    reason: 'all-attempts-exhausted',
  }
}

export async function dispatchWalkthrough(
  apiResponse: ApiResponse,
  paramMap: ParamMap = {},
  options: {confirmed?: boolean} = {}
) {
  if (!apiResponse || !apiResponse.result || !Array.isArray(apiResponse.result.walkthrough)) {
    return false
  }

  const steps = apiResponse.result.walkthrough

  const first = steps[0]
  const rawPath: string | undefined = first?.path
  const [base, hash] = (rawPath || '').split('#')
  const resolvedBase = base ? replaceParamsInPath(base, paramMap) : ''
  let resolvedPath = resolvedBase + (hash ? `#${hash}` : '')

  const looksLikeTimetable =
    resolvedPath.startsWith('/timetable') ||
    (first && typeof first.path === 'string' && first.path.includes('/timetable')) ||
    (first && typeof first.name === 'string' && first.name.toLowerCase().includes('timetable'))

  if (looksLikeTimetable) {
    const forced = '/institute/schedule/timetable'
    resolvedPath = forced
  }

  const preferredSelectors: string[] = []
  try {
    const candidate = first?.walkthrough?.target || first?.target || (first as any)?.selector
    if (candidate && typeof candidate === 'string') {
      const c = candidate.trim()
      if (c) {
        preferredSelectors.push(c)
        preferredSelectors.push(`#root ${c}`)
        preferredSelectors.push(`main ${c}`)
      }
    }
  } catch (e) {
    calculateErrorMessage(e)
  }

  const shouldWaitForever = looksLikeTimetable
  const componentName = looksLikeTimetable ? 'AITimetable' : undefined

  const cancelTimer = window.setTimeout(
    () => {
      try {
        window.dispatchEvent(new CustomEvent('walkthrough-cancel'))
      } catch (e) {
        calculateErrorMessage(e)
      }
    },
    3 * 60 * 1000
  )
  // show a lightweight overlay early while we navigate to the target page
  try {
    window.dispatchEvent(
      new CustomEvent('dynamic-walkthrough-overlay-show', {detail: {apiResponse, paramMap}})
    )
  } catch (e) {
    calculateErrorMessage(e)
  }

  const navResult = await navigateAndWaitForContent(
    resolvedPath,
    3,
    500,
    preferredSelectors,
    shouldWaitForever,
    componentName
  )

  try {
    clearTimeout(cancelTimer)
  } catch (e) {
    calculateErrorMessage(e)
  }

  if (!navResult.success) {
    try {
      // remove our overlay and notify listeners so they can resume if needed
      window.dispatchEvent(new CustomEvent('dynamic-walkthrough-overlay-prestart'))
    } catch (e) {
      calculateErrorMessage(e)
    }

    window.dispatchEvent(
      new CustomEvent('walkthrough-error', {
        detail: {
          messageId: 'WALKTHROUGH.NAV.TARGET_PAGE_LOAD_TIMEOUT',
          messageParams: {}, // if needed
          technical: {
            resolvedPath,
            paramMap,
            attemptedSelectors: preferredSelectors,
          },
        },
      })
    )
    return false
  }
  closeChatIfOpen()

  await new Promise((r) => setTimeout(r, 200))

  try {
    try {
      window.dispatchEvent(
        new CustomEvent('dynamic-walkthrough-overlay-prestart', {detail: {apiResponse, paramMap}})
      )
    } catch (e) {
      calculateErrorMessage(e)
    }

    const ev = new CustomEvent('dynamic-walkthrough-start', {
      detail: {apiResponse, paramMap, confirmed: options.confirmed},
    })
    window.dispatchEvent(ev)
    return true
  } catch (err) {
    calculateErrorMessage(err)
    return false
  }
}

export default dispatchWalkthrough
